import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Hourglass, Warning } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ACTIONS = [
  ["relocation_offered", "Relocation offered", "A post at comparable terms was offered, including one further away."],
  ["relocation_refused", "Relocation refused", "The guard declined the post offered. This is the agency's defence — say which post."],
  ["separation_initiated", "Separation initiated", "Terminated with separation pay before the clock ran out."],
  ["recalled_to_pool", "Recalled to the pool", "Returned to the reliever pool pending a detail."],
];

const TONE = {
  breach: "border-[hsl(var(--alert))] text-[hsl(var(--alert))]",
  critical: "border-[hsl(var(--alert))] text-[hsl(var(--alert))]",
  warning: "border-[hsl(var(--warning))] text-[hsl(var(--warning))]",
};

/**
 * The six-month clock.
 *
 * ⚠️ A guard left off-detail for more than six continuous months is
 * CONSTRUCTIVELY DISMISSED under DO 150-16 and settled Supreme Court doctrine —
 * backwages plus separation pay, per guard, whether or not anybody meant it.
 * Every fact needed to see it coming was already on file and nothing counted the
 * days, so a guard parked for seven months looked exactly like one who worked
 * yesterday: the only trace of their absence was the absence of rows.
 *
 * The clock is DERIVED on every read, never stored. A stored counter drifts the
 * moment somebody edits a shift, and this is the one number that has to be right
 * on the day a labour arbiter asks for it.
 */
export default function Reserved() {
  const [data, setData] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ action: "relocation_offered", note: "", effective_date: "" });

  const load = (all) =>
    api.get(`/deployment/reserved${all ? "?all=true" : ""}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(errMsg(e)));

  useEffect(() => { load(showAll); }, [showAll]);

  const save = async () => {
    try {
      await api.post(`/guards/${sel.guard_id}/reserved-decision`, {
        ...form, effective_date: form.effective_date || null,
      });
      toast.success("Decision recorded");
      setSel(null);
      setForm({ action: "relocation_offered", note: "", effective_date: "" });
      load(showAll);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const c = data?.counts || {};
  const t = data?.thresholds || {};

  const columns = [
    { key: "name", label: "Guard", render: (r) => (
      <div>
        <div className="font-semibold">{r.name}</div>
        <div className="font-mono text-[10px] text-muted-foreground">{r.employee_no} · {r.position || "Security Guard"}</div>
      </div>
    ) },
    { key: "employment_status", label: "Employment", render: (r) => <StatusBadge value={r.employment_status} /> },
    { key: "state", label: "Reserved status", render: (r) => (
      <span className={`inline-block border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${TONE[r.state] || "border-border text-muted-foreground"}`}>
        {r.state.replace(/_/g, " ")}
      </span>
    ) },
    { key: "days_off_detail", label: "Days off-detail", render: (r) => (
      r.days_off_detail == null
        ? <span className="text-muted-foreground">unknown</span>
        : <span className="font-mono text-sm font-bold tabular-nums">{r.days_off_detail}</span>
    ) },
    { key: "since", label: "Since", render: (r) => (
      <div>
        <div className="font-mono text-xs">{r.since || "—"}</div>
        <div className="text-[10px] text-muted-foreground">{r.since_kind}</div>
      </div>
    ) },
    { key: "breach_on", label: "Six months falls on", render: (r) => (
      r.state === "on_detail"
        ? <span className="text-xs text-muted-foreground">
            next detail {r.next_detail?.date} · {r.next_detail?.site_name}
          </span>
        : r.breach_on
          ? <div>
              <div className={`font-mono text-xs ${r.days_until_breach <= 0 ? "text-[hsl(var(--alert))] font-bold" : ""}`}>{r.breach_on}</div>
              <div className="text-[10px] text-muted-foreground">
                {r.days_until_breach > 0 ? `${r.days_until_breach} day(s) left` : `${-r.days_until_breach} day(s) OVER`}
              </div>
            </div>
          : <span className="text-muted-foreground">—</span>
    ) },
    { key: "decision", label: "Last decision", render: (r) => (
      r.decision
        ? <div className="text-xs">
            <div className="font-semibold">{r.decision.action.replace(/_/g, " ")}</div>
            <div className="text-[10px] text-muted-foreground">{r.decision.effective_date} · {r.decision.by}</div>
          </div>
        : <span className="text-muted-foreground text-xs">none</span>
    ) },
    { key: "actions", label: "", render: (r) => (
      r.state === "on_detail" ? null : (
        <Button size="sm" variant="outline" className="rounded-none h-7 text-[11px]"
                data-testid={`decide-${r.employee_no}`}
                onClick={(e) => { e.stopPropagation(); setSel(r); }}>
          Record a decision
        </Button>
      )
    ) },
  ];

  return (
    <div data-testid="page-reserved">
      <PageHeader
        title="Reserved status"
        subtitle="The six-month clock. A guard off-detail past it is constructively dismissed."
        action={
          <Button variant="outline" className="rounded-none" data-testid="toggle-all"
                  onClick={() => setShowAll((v) => !v)}>
            {showAll ? "Only those off-detail" : "Show everybody"}
          </Button>
        }
      />

      <div className="grid md:grid-cols-4 gap-3 mb-4">
        <Tile label="Off-detail" value={(c.off_detail || 0) + (c.warning || 0) + (c.critical || 0) + (c.breach || 0)} />
        <Tile label={`Warning (${t.warning}d+)`} value={c.warning || 0} tone={c.warning ? "warning" : null} />
        <Tile label={`Critical (${t.critical}d+)`} value={c.critical || 0} tone={c.critical ? "alert" : null} />
        <Tile label={`In breach (${t.breach}d+)`} value={c.breach || 0} tone={c.breach ? "alert" : null} />
      </div>

      {data?.in_breach > 0 && (
        <div className="border border-[hsl(var(--alert))] bg-[hsl(var(--alert))]/10 p-3 mb-4 flex gap-2" data-testid="reserved-breach-banner">
          <Warning size={18} weight="fill" className="text-[hsl(var(--alert))] shrink-0 mt-0.5" />
          <div className="text-xs leading-snug">
            <span className="font-bold">
              {data.in_breach} guard{data.in_breach > 1 ? "s have" : " has"} been off-detail for more than
              six months.
            </span>{" "}
            That is constructive dismissal under DO 150-16 — backwages and separation pay, per guard, whether
            or not it was intended. Repost them, offer a relocation, or separate them with pay, and record
            which.
          </div>
        </div>
      )}

      {c.unknown > 0 && (
        <div className="border border-border p-3 mb-4 text-xs text-muted-foreground" data-testid="reserved-unknown">
          {c.unknown} guard(s) have no detail and no hire date on file, so the clock cannot be computed for
          them. That is <span className="text-foreground font-semibold">not</span> the same as being fine —
          put a hire date on their 201 file.
        </div>
      )}

      <DataTable columns={columns} rows={data?.rows || []} testid="reserved-table"
                 empty="Nobody is off-detail. Every guard has a post or one booked." />

      <div className="border border-border bg-card p-4 mt-5 text-xs text-muted-foreground leading-relaxed max-w-3xl">
        <div className="flex items-center gap-2 font-heading font-bold text-foreground text-sm mb-2">
          <Hourglass size={16} /> The three ways the clock stops
        </div>
        <p className="mb-1.5">
          <span className="text-foreground font-semibold">Repost them.</span> Give the guard a detail on the{" "}
          <Link to="/schedule" className="underline">Schedule</Link>. The clock resets by itself the moment a
          shift exists — that is why reposting is not a decision you record here. The roster is the record,
          and a note claiming somebody was reposted with no shift behind it is worse than nothing, because it
          looks like a defence and is not one.
        </p>
        <p className="mb-1.5">
          <span className="text-foreground font-semibold">Offer a relocation</span> at comparable terms,
          including a post further away, and record it. A refused offer is the agency&apos;s defence, so name
          the post you offered.
        </p>
        <p>
          <span className="text-foreground font-semibold">Separate them with pay</span> before the six months
          run out. Final pay is computed from{" "}
          <Link to="/guards" className="underline">the 201 file</Link>.
        </p>
      </div>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">
            {sel?.name} — {sel?.days_off_detail} day(s) off-detail
          </DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase font-bold">What was done</Label>
              <Select value={form.action} onValueChange={(v) => setForm({ ...form, action: v })}>
                <SelectTrigger data-testid="decision-action" className="rounded-none mt-1 h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACTIONS.map(([v, label]) => <SelectItem key={v} value={v}>{label}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-[10px] text-muted-foreground mt-1 leading-snug">
                {ACTIONS.find(([v]) => v === form.action)?.[2]}
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold">Note</Label>
              <Input data-testid="decision-note" className="rounded-none mt-1 h-9"
                     placeholder="Which post, offered by whom, and what the guard said"
                     value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold">Effective date</Label>
              <Input type="date" data-testid="decision-date" className="rounded-none mt-1 h-9 font-mono"
                     value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} />
            </div>
          </div>
          <Button className="w-full rounded-none h-11 font-heading font-bold" data-testid="decision-save"
                  disabled={!form.note.trim()} onClick={save}>
            RECORD IT
          </Button>
          <div className="text-[10px] text-muted-foreground text-center leading-snug">
            The note is the record. A decision with no detail in it defends nothing.
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Tile({ label, value, tone }) {
  const border = tone === "alert" ? "border-[hsl(var(--alert))]"
    : tone === "warning" ? "border-[hsl(var(--warning))]" : "border-border";
  return (
    <div className={`border ${border} bg-card p-3`}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{label}</div>
      <div className="font-heading font-black text-2xl tabular-nums">{value}</div>
    </div>
  );
}
